import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LinkPreview } from "@bt-common/schemas/bt/user-message";
import { Template } from "@common/models/template";
import { Asset, RetryLimitUnit } from "@common/types/interface";
import { Poll } from "@extension/context/content/components/add-scheduled-message-button/schedule-message-types";
import { CommonRootState } from '@common/types/common-root-state-type';

export interface ScheduledMessageEditorState {
  scheduleText: string;
  message: string;
  isPoll: boolean;
  poll: Partial<Poll>;
  asset?: Asset;
  imageId: string;
  sendLinkPreview: boolean;
  linkPreview: LinkPreview;
  templateId: string;
  templateName: string;
  dueDate: string;
  // BE#657 — true only once the user has EXPLICITLY picked a date/time (via the
  // date/time picker or "set to now"). A fresh/opened draft is false even if some
  // entry point seeded `dueDate` to ~now, so a message is never sendable until the
  // user actually chose a time. Gated on by the Send gate + the schedule thunk.
  dueDateSetByUser: boolean;
  rruleset: string;
  isRecurring: boolean;
  cancelIfReceived: boolean;
  retryLimitDuration: number | undefined;
  retryLimitUnit: RetryLimitUnit | undefined;

  // UI coordination state
  isEmojiPickerOpen: boolean;
  currentListMode: 'none' | 'bullet' | 'number';
  isInListMode: boolean;
  activeFormats: {
    bold: boolean;
    italic: boolean;
    strike: boolean;
    code: boolean;
    codeBlock: boolean;
    quote: boolean;
  };
  formatRequest: string | null; // Symbol to format: '*' | '~' | '_' | '```' | '`' | 'bullet' | 'number' | 'quote'

  // Context configuration
  editorContext: 'scheduleMessage' | 'scheduleCampaign';
  showRecurrence: boolean;
  recurrenceText: string;
  snackbarMessage: string | null;

  // Change tracking
  hasUnsavedChanges: boolean;
  originalTemplateData: Partial<Template> | null;

  // File upload state
  isFileUploading: boolean;

  // FE#312: the time input currently holds a partial/invalid value (typed but not
  // yet a complete, in-range HH:mm). Blocks submit until it is valid.
  timeInputInvalid: boolean;
}

const initialState: ScheduledMessageEditorState = {
  scheduleText: '',
  message: '',
  isPoll: false,
  poll: {},
  // asset is undefined by default
  imageId: '',
  sendLinkPreview: false,
  linkPreview: {},
  templateId: '',
  templateName: '',
  dueDate: '',
  dueDateSetByUser: false,
  rruleset: '',
  isRecurring: false,
  cancelIfReceived: false,
  retryLimitDuration: undefined,
  retryLimitUnit: undefined,

  // UI coordination state
  isEmojiPickerOpen: false,
  currentListMode: 'none',
  isInListMode: false,
  activeFormats: {
    bold: false,
    italic: false,
    strike: false,
    code: false,
    codeBlock: false,
    quote: false,
  },
  formatRequest: null,

  // Context configuration
  editorContext: 'scheduleMessage',
  showRecurrence: false,
  recurrenceText: '',
  snackbarMessage: null,

  // Change tracking
  hasUnsavedChanges: false,
  originalTemplateData: null,

  // File upload state
  isFileUploading: false,
  timeInputInvalid: false,
};

const ScheduledMessageEditorSlice = createSlice({
  name: "ScheduledMessageEditor",
  initialState,
  reducers: {
    // Individual field setters
    setScheduleText: (state, action: PayloadAction<string>) => {
      state.scheduleText = action.payload;
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && action.payload !== (state.originalTemplateData.message || '')) {
        state.hasUnsavedChanges = true;
      }
    },
    setIsPoll: (state, action: PayloadAction<boolean>) => {
      state.isPoll = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && action.payload !== (state.originalTemplateData.isPoll || false)) {
        state.hasUnsavedChanges = true;
      }
    },
    setPoll: (state, action: PayloadAction<Partial<Poll>>) => {
      state.poll = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && JSON.stringify(action.payload) !== JSON.stringify(state.originalTemplateData.poll || {})) {
        state.hasUnsavedChanges = true;
      }
    },
    setAsset: (state, action: PayloadAction<Asset | undefined>) => {
      state.asset = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && JSON.stringify(action.payload) !== JSON.stringify(state.originalTemplateData.asset)) {
        state.hasUnsavedChanges = true;
      }
    },
    setImageId: (state, action: PayloadAction<string>) => {
      state.imageId = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && action.payload !== (state.originalTemplateData.imageId || '')) {
        state.hasUnsavedChanges = true;
      }
    },
    setSendLinkPreview: (state, action: PayloadAction<boolean>) => {
      state.sendLinkPreview = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && action.payload !== (state.originalTemplateData.sendLinkPreview || false)) {
        state.hasUnsavedChanges = true;
      }
    },
    setLinkPreview: (state, action: PayloadAction<LinkPreview>) => {
      state.linkPreview = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && JSON.stringify(action.payload) !== JSON.stringify(state.originalTemplateData.linkPreview || {})) {
        state.hasUnsavedChanges = true;
      }
    },
    setTemplateId: (state, action: PayloadAction<string>) => {
      state.templateId = action.payload;
    },
    setTemplateName: (state, action: PayloadAction<string>) => {
      state.templateName = action.payload;
      // Auto-detect changes
      if (state.originalTemplateData && action.payload !== (state.originalTemplateData.name || '')) {
        state.hasUnsavedChanges = true;
      }
    },
    setDueDate: (state, action: PayloadAction<string>) => {
      state.dueDate = action.payload;
      // BE#657 — setDueDate is dispatched only by explicit user actions now (the
      // date/time pickers, the recurrence commit, and the footer "set it to now").
      // The now-seeding effects were removed, so reaching here means the user chose
      // a time → the draft becomes eligible to send.
      state.dueDateSetByUser = true;
    },
    // BE#657 M2 — a deliberate user interaction with the date/time picker that lands
    // on the SAME value already shown still counts as an explicit confirmation. The
    // pickers swallow a same-value change (loop guard) before setDueDate runs, so they
    // dispatch this to raise the flag without re-writing dueDate — otherwise a user who
    // accepts a pre-seeded date (calendar slot / first-week / default-time) could never
    // enable Send without first changing the value to something else and back.
    markDueDateSetByUser: (state) => {
      state.dueDateSetByUser = true;
    },
    setRruleset: (state, action: PayloadAction<string>) => {
      state.rruleset = action.payload;
    },
    setIsRecurring: (state, action: PayloadAction<boolean>) => {
      state.isRecurring = action.payload;
    },
    setCancelIfReceived: (state, action: PayloadAction<boolean>) => {
      state.cancelIfReceived = action.payload;
    },
    setRetryLimitDuration: (state, action: PayloadAction<number | undefined>) => {
      state.retryLimitDuration = action.payload;
    },
    setRetryLimitUnit: (state, action: PayloadAction<RetryLimitUnit | undefined>) => {
      state.retryLimitUnit = action.payload;
    },

    // UI coordination actions
    setIsEmojiPickerOpen: (state, action: PayloadAction<boolean>) => {
      state.isEmojiPickerOpen = action.payload;
    },
    setCurrentListMode: (state, action: PayloadAction<'none' | 'bullet' | 'number'>) => {
      state.currentListMode = action.payload;
    },
    setIsInListMode: (state, action: PayloadAction<boolean>) => {
      state.isInListMode = action.payload;
    },
    setActiveFormats: (state, action: PayloadAction<Partial<ScheduledMessageEditorState['activeFormats']>>) => {
      state.activeFormats = { ...state.activeFormats, ...action.payload };
    },
    setFormatRequest: (state, action: PayloadAction<string | null>) => {
      state.formatRequest = action.payload;
    },

    // Context configuration actions
    setEditorContext: (state, action: PayloadAction<'scheduleMessage' | 'scheduleCampaign'>) => {
      state.editorContext = action.payload;
    },
    setShowRecurrence: (state, action: PayloadAction<boolean>) => {
      state.showRecurrence = action.payload;
    },
    setRecurrenceText: (state, action: PayloadAction<string>) => {
      state.recurrenceText = action.payload;
    },
    setSnackbarMessage: (state, action: PayloadAction<string | null>) => {
      state.snackbarMessage = action.payload;
    },

    // Change tracking actions
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setOriginalTemplateData: (state, action: PayloadAction<Partial<Template> | null>) => {
      state.originalTemplateData = action.payload;
    },

    // File upload state actions
    setIsFileUploading: (state, action: PayloadAction<boolean>) => {
      state.isFileUploading = action.payload;
    },

    // FE#312: time-input validity (blocks submit while a partial time is typed)
    setTimeInputInvalid: (state, action: PayloadAction<boolean>) => {
      state.timeInputInvalid = action.payload;
    },
    markAsChanged: (state) => {
      state.hasUnsavedChanges = true;
    },
    markAsSaved: (state) => {
      state.hasUnsavedChanges = false;
    },

    resetScheduledMessageEditor: (state) => {
      return {
        ...initialState,
        editorContext: state.editorContext, // Preserve current editorContext
      };
    },
    loadTemplateData: (state, action: PayloadAction<Partial<Template>>) => {
      const template = action.payload;
      // Load all template fields into the editor state
      state.message = template.message || '';
      state.isPoll = template.isPoll || false;
      state.poll = template.poll;
      // Template.asset is bt-common's MessageAsset (name/type nullable) but
      // the editor state holds the input-form Asset (type required). When
      // loading a persisted template, missing/null type is treated as
      // empty-string ("auto-detect"). Narrowing at the consumer boundary
      // per #41 decision — input-form Asset stays the editor's write-shape.
      state.asset = template.asset
        ? ({
            url: template.asset.url,
            name: template.asset.name ?? '',
            type: template.asset.type ?? '',
          } as Asset)
        : undefined;
      state.imageId = template.imageId || '';
      state.sendLinkPreview = template.sendLinkPreview || false;
      state.linkPreview = template.linkPreview ?? {};
      state.templateId = template._id ?? '';
      state.templateName = template.name || '';
      
      // Store original data for change detection and reset change flag
      state.originalTemplateData = template;
      state.hasUnsavedChanges = false;
    },
    loadMessageData: (state, action: PayloadAction<any>) => {
      const msg = action.payload;
      state.message = msg.message || '';
      state.isPoll = msg.isPoll || false;
      state.poll = msg.poll || {};
      state.asset = msg.asset; // Correctly allow undefined
      state.imageId = msg.imageId || '';
      state.sendLinkPreview = msg.sendLinkPreview || false;
      state.linkPreview = msg.linkPreview || {};
      state.dueDate = msg.dueDate || '';
      // BE#657 — a fresh editor load is NOT a user pick. Carry an explicit prior
      // user-set flag when editing an existing message (msg.dueDateSetByUser), else
      // false — so a seeded/opened draft stays non-sendable until the user picks.
      state.dueDateSetByUser = !!msg.dueDateSetByUser;
      state.rruleset = msg.rruleset || '';
      state.isRecurring = msg.isRecurring || false;
      state.cancelIfReceived = msg.cancelIfReceived || false;
      state.retryLimitDuration = msg.retryLimitDuration;
      state.retryLimitUnit = msg.retryLimitUnit;
      
      // Unset template info since this is a direct message load
      state.templateId = '';
      state.templateName = '';
      state.originalTemplateData = null;
      state.hasUnsavedChanges = false;
    },
  },
});

// Selectors

// Helper function to calculate the maximum height for the message editor
const calculateMessageEditorMaxHeight = (sendLinkPreview: boolean, hasReplyData: boolean): string => {
  // Base heights: 380px without link preview, 188px with link preview
  const baseHeight = sendLinkPreview ? 188 : 230;

  // Subtract 83px if replying to another message (same as ReplyToMessageChip height)
  const adjustedHeight = hasReplyData ? baseHeight - 70 : baseHeight;

  return `${adjustedHeight}px`;
};

// Selector to check if there's reply data (same conditions as ReplyToMessageChip)
export const selectHasReplyData = createSelector(
  (state: CommonRootState) => state.WhatsAppReducer.isReply,
  (localReplyData) => !!localReplyData
);

// Selector to get the calculated max height for the message editor
export const selectMessageEditorMaxHeight = createSelector(
  (state: CommonRootState) => state.ScheduledMessageEditorReducer.sendLinkPreview,
  selectHasReplyData,
  (sendLinkPreview, hasReplyData) => calculateMessageEditorMaxHeight(sendLinkPreview, hasReplyData)
);



export const scheduledMessageEditorActions = ScheduledMessageEditorSlice.actions;

export const {
  setScheduleText,
  setMessage,
  setIsPoll,
  setPoll,
  setAsset,
  setImageId,
  setSendLinkPreview,
  setLinkPreview,
  setTemplateId,
  setTemplateName,
  setDueDate,
  markDueDateSetByUser,
  setRruleset,
  setIsRecurring,
  setCancelIfReceived,
  setRetryLimitDuration,
  setRetryLimitUnit,

  // UI coordination actions
  setIsEmojiPickerOpen,
  setCurrentListMode,
  setIsInListMode,
  setActiveFormats,
  setFormatRequest,

  // Context configuration actions
  setEditorContext,
  setShowRecurrence,
  setRecurrenceText,
  setSnackbarMessage,

  // Change tracking actions
  setHasUnsavedChanges,
  setOriginalTemplateData,
  markAsChanged,
  markAsSaved,

  // File upload state actions
  setIsFileUploading,
  setTimeInputInvalid,

  resetScheduledMessageEditor,
  loadTemplateData,
  loadMessageData,
} = ScheduledMessageEditorSlice.actions;

export default ScheduledMessageEditorSlice.reducer; 